import { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import { Filters } from './Filters';
import { ActivityCard } from '../../../components/ActivityCard';
import { ShareIntelModal } from '../ShareIntelModal';
import { showErrorMessage } from 'helpers';
import { getAgentIntelFeed, getAgentTeamIntelTags } from 'api/intel/agent';
import useScrollPagination from 'hooks/use-scroll-pagination';
import { Spinner } from 'components';
import { useDispatch, useSelector } from 'react-redux';
import { TEAM_ADMIN, TEAM_BASIC } from 'settings/constants/roles';
import { getAgentTeamRoleSelector } from 'store/selectors/agentTeamDetail';
import { getAgentClientsList } from 'api/clientsList';
import { requestGetTeamListEffect } from 'store/effects';
import { getActiveTeamList } from 'store/selectors/teamList';
import { getUserId } from 'store/selectors/user';
import {
  AgentIntelActivitySearchParamsType,
  AgentIntelFiltersType,
  FeedVisibility,
  PreparedAgentIntelActivityDataType,
} from 'types/intel';
import moment from 'moment';

type ActivityFeedPropsType = {
  masterVisibilitySwitch: FeedVisibility;
};

const initialFilters: AgentIntelFiltersType = {
  keyword: '',
  locations: [],
  dateRange: {
    to: undefined,
    from: undefined,
  },
  tags: [],
  agents: [],
};

export const ActivityFeed = (props: ActivityFeedPropsType) => {
  const dispatch = useDispatch();
  const { masterVisibilitySwitch } = props;
  const pageSize = 20;
  const scrollElementId = 'agent-intel-feed';
  const [openShareModal, setOpenShareModal] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [newPosts, setNewPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [otherLoading, setOtherLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [systemTags, setSystemTags] = useState<any[]>([]);
  const [allClients, setAllClients] = useState<any[]>([]);
  const currentUserId = useSelector(getUserId);
  const agentRole = useSelector(getAgentTeamRoleSelector);
  const teamMembers = useSelector(getActiveTeamList);
  const [filters, setFilters] = useState<AgentIntelFiltersType>(initialFilters);
  const [activeFilterSection, setActiveFilterSection] = useState<string | null>(null);

  useEffect(() => {
    fetchRelevantData();
  }, []);

  useEffect(() => {
    const scrollElement = document.getElementById(scrollElementId);
    if (scrollElement)
      scrollElement.scrollTo({
        top: 0,
        behavior: 'auto',
      });
    setData([]);
    setNewPosts([]);
    fetchAgentIntelFeed(true);
  }, [masterVisibilitySwitch, filters]);

  const fetchAgentIntelFeed = async (isFirstPage: boolean) => {
    setLoading(true);
    try {
      const searchParams = getSearchParamsObject(isFirstPage);
      const response = await getAgentIntelFeed(searchParams);
      if (response.status === 200) {
        const results = response.data?.data?.results ?? [];
        const preparedResults: PreparedAgentIntelActivityDataType[] = results.map((item) => {
          return {
            id: item.Id,
            user: {
              name: `${item.CreatedByUser.FirstName}${
                item.CreatedByUser.LastName ? ` ${item.CreatedByUser.LastName}` : ''
              }`,
              role: 'Agent',
              avatar: item.CreatedByUser.AvatarUrl,
            },
            createdAt: item.CreatedDate,
            title: item.Subject,
            description: item.Description,
            images: item.Images?.map((image) => image.Url) ?? [],
            publishedTo: getPublishedToInfo(item),
            mediaLink: item.MediaLink,
          };
        });
        if (isFirstPage) {
          setData(preparedResults);
          setPageNumber(1);
        } else {
          setData((prev) => [...prev, ...preparedResults]);
          setPageNumber((prev) => prev + 1);
        }
        const dbTotalResults = response.data?.data?.total ?? 0;
        if (dbTotalResults > 0) {
          const dbTotalPages = dbTotalResults / pageSize;
          if (Number.isInteger(dbTotalPages)) setTotalPages(dbTotalPages);
          else setTotalPages(Math.floor(dbTotalPages) + 1);
        }
      }
    } catch (e) {
      showErrorMessage('Unable to fetch intel feed');
    }
    setLoading(false);
  };

  const fetchRelevantData = async () => {
    setOtherLoading(true);
    if (agentRole !== TEAM_BASIC) dispatch(requestGetTeamListEffect());
    await fetchTags();
    await fetchClients();
    setOtherLoading(false);
  };

  const fetchTags = async () => {
    try {
      const response = await getAgentTeamIntelTags();
      if (response.status === 200 && response.data?.data?.length) setSystemTags(response.data.data);
    } catch (e) {
      showErrorMessage('Unable to fetch tags.');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await getAgentClientsList();
      if (response.status === 200 && response.data?.result?.length)
        setAllClients(response.data.result);
    } catch (e) {
      showErrorMessage('Unable to fetch clients.');
    }
  };

  const getPublishedToInfo = (intel) => {
    if (intel.ActivityClients?.length) {
      const stringified: string[] = intel.ActivityClients.map(
        (item) =>
          `${item.ClientUser.FirstName}${
            item.ClientUser.LastName ? ` ${item.ClientUser.LastName}` : ''
          }`,
      );

      return {
        type: 'CLIENT',
        data: stringified,
      };
    }

    if (intel.ActivityLocations?.length) {
      const stringified: string[] = intel.ActivityLocations.map((item) => {
        if (item.Location?.City && item.Location?.State)
          return `${item.Location.City}, ${item.Location.State}`;
        if (!item.Location?.City && item.Location?.State) return item.Location.State;
        if (item.Location?.City && !item.Location?.State) return item.Location.City;
        if (!item.Location?.City && !item.Location?.State) return '';
      });

      return {
        type: 'AREA',
        data: stringified,
      };
    }

    return 'ALL';
  };

  const handleShareIntel = () => {
    setOpenShareModal(true);
  };

  const handleCloseShareModal = () => {
    setOpenShareModal(false);
  };

  const getAgentsFilter = () => {
    if (agentRole === TEAM_BASIC || masterVisibilitySwitch === FeedVisibility.User) return [];

    const userFilteredAgents = filters.agents
      .filter((item) => item.active)
      .map((item) => item.value);

    if (userFilteredAgents.length) return userFilteredAgents;

    const managedTeamMembers =
      agentRole === TEAM_ADMIN
        ? teamMembers.filter((member) => member.IsManagedByCurrentUser)
        : teamMembers;

    return masterVisibilitySwitch === FeedVisibility.Team
      ? managedTeamMembers
          .filter((member) => member.id !== currentUserId)
          .map((member) => member.id)
      : [...managedTeamMembers.map((member) => member.id), currentUserId];
  };

  const getSearchParamsObject = (isFirstPage: boolean): AgentIntelActivitySearchParamsType => {
    const searchAgentIds: number[] = getAgentsFilter();

    const obj = {
      pageSize,
      page: isFirstPage ? 1 : pageNumber + 1,
      ...(searchAgentIds.length && { agentIds: searchAgentIds }),
      ...((agentRole === TEAM_BASIC || masterVisibilitySwitch === FeedVisibility.User) &&
      filters.tags.filter((item) => item.active).length
        ? { tags: filters.tags.filter((item) => item.active).map((item) => item.label) }
        : {}),
      ...(filters.keyword && { searchText: filters.keyword }),
      ...(filters.locations.filter((item) => item.active).length && {
        locations: filters.locations
          .filter((item) => item.active)
          .map((loc) => ({
            State: loc.location.State,
            City: loc.location.City,
          })),
      }),
      ...(filters.dateRange.from && {
        fromDate: moment(filters.dateRange.from).startOf('day').toISOString(),
      }),
      ...(filters.dateRange.to && {
        toDate: moment(filters.dateRange.to).endOf('day').toISOString(),
      }),
    };

    return obj;
  };

  const handleNewPost = (payload) => {
    const preparedResult: PreparedAgentIntelActivityDataType = {
      id: payload.Id,
      user: {
        name: `${payload.CreatedByUser.FirstName}${
          payload.CreatedByUser.LastName ? ` ${payload.CreatedByUser.LastName}` : ''
        }`,
        role: 'Agent',
        avatar: payload.CreatedByUser.AvatarUrl,
      },
      createdAt: payload.CreatedDate,
      title: payload.Subject,
      description: payload.Description,
      images: payload.Images?.map((image) => image.Url) ?? [],
      publishedTo: getPublishedToInfo(payload) as any,
      mediaLink: payload.MediaLink,
      isNew: true,
    };

    setNewPosts((prev) => [preparedResult, ...prev]);
  };

  useScrollPagination({
    container: scrollElementId,
    isFetching: loading,
    pageNumber,
    onScrollEnd: () => fetchAgentIntelFeed(false),
    totalPages,
  });

  return (
    <div
      className={styles.pageContent}
      id={scrollElementId}
      onScroll={() => setActiveFilterSection(null)}
    >
      <div className={styles.activityWrapper}>
        {!loading && !data.length && !newPosts.length ? (
          <div className={styles.noDataFound}>
            <p className={styles.noDataTitle}>You’re all caught up!</p>
            <p className={styles.noDataDescription}>You have no new posts in the Intel page.</p>
          </div>
        ) : null}
        {data.length || newPosts.length
          ? [...newPosts, ...data].map((activity) => (
              <ActivityCard key={activity.id} activity={activity} />
            ))
          : null}
        {loading ? (
          <div className={styles.loader}>
            <Spinner />
          </div>
        ) : null}
      </div>
      <div className={styles.filterWrapper}>
        <button className={styles.shareIntelButton} onClick={handleShareIntel}>
          Share Intel
        </button>
        <Filters
          systemTags={systemTags}
          teamMembers={teamMembers}
          filters={filters}
          setFilters={setFilters}
          masterVisibilitySwitch={masterVisibilitySwitch}
          activeFilterSection={activeFilterSection}
          setActiveFilterSection={setActiveFilterSection}
        />
        <ShareIntelModal
          open={openShareModal}
          onClose={handleCloseShareModal}
          systemTags={systemTags}
          allClients={allClients}
          loading={otherLoading}
          handleNewPost={handleNewPost}
        />
      </div>
    </div>
  );
};
